import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

const PartyContext = createContext();

export function useParties() {
  return useContext(PartyContext);
}

export function PartyProvider({ children }) {
  const { currentUser } = useAuth();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch parties for the current user
  const fetchParties = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // Query parties where the current user is the creator or an invitee
      const partiesQuery = query(
        collection(db, 'parties'),
        where('creatorId', '==', currentUser.uid),
        orderBy('date', 'asc')
      );
      
      const querySnapshot = await getDocs(partiesQuery);
      const partiesData = [];
      
      querySnapshot.forEach((doc) => {
        partiesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setParties(partiesData);
    } catch (error) {
      console.error('PartyContext: Error fetching parties:', error);
      
      let errorMessage = 'Failed to fetch parties. Please try again.';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check Firestore security rules.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Missing Firestore index. Please create the required index.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firestore is currently unavailable. Please try again later.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      setParties([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Create a new party
  const createParty = useCallback(async (partyData) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to create a party');
    }

    try {
      const newParty = {
        name: partyData.name.trim(),
        date: partyData.date,
        location: partyData.location.trim(),
        notes: partyData.notes.trim(),
        groupId: partyData.groupId,
        groupName: partyData.groupName,
        creatorId: currentUser.uid,
        creatorName: currentUser.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'upcoming',
        invitees: [], // Will be populated when invitation system is implemented
        rsvps: {}, // Will be populated when RSVP system is implemented
        maxAttendees: partyData.maxAttendees || null,
        isPrivate: partyData.isPrivate || false
      };

      // Add party to Firestore
      const docRef = await addDoc(collection(db, 'parties'), newParty);
      
      // Add the new party to local state
      const createdParty = {
        id: docRef.id,
        ...newParty,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      setParties(prev => [createdParty, ...prev]);
      
      return createdParty;
    } catch (error) {
      console.error('PartyContext: Error creating party:', error);
      throw new Error('Failed to create party. Please try again.');
    }
  }, [currentUser]);

  // Update an existing party
  const updateParty = useCallback(async (partyId, updates) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to update a party');
    }

    try {
      const partyRef = doc(db, 'parties', partyId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(partyRef, updateData);
      
      // Update local state
      setParties(prev => prev.map(party => 
        party.id === partyId 
          ? { ...party, ...updates, updatedAt: new Date() }
          : party
      ));
      
      console.log('PartyContext: Party updated successfully:', partyId);
    } catch (error) {
      console.error('PartyContext: Error updating party:', error);
      throw new Error('Failed to update party. Please try again.');
    }
  }, [currentUser]);

  // Delete a party
  const deleteParty = useCallback(async (partyId) => {
    if (!currentUser) {
      throw new Error('User must be authenticated to delete a party');
    }

    try {
      // Check if user is the creator of the party
      const party = parties.find(p => p.id === partyId);
      if (!party) {
        throw new Error('Party not found');
      }
      
      if (party.creatorId !== currentUser.uid) {
        throw new Error('Only party creators can delete parties');
      }

      // Delete party from Firestore
      await deleteDoc(doc(db, 'parties', partyId));
      
      // Remove from local state
      setParties(prev => prev.filter(party => party.id !== partyId));
      
      console.log('PartyContext: Party deleted successfully:', partyId);
    } catch (error) {
      console.error('PartyContext: Error deleting party:', error);
      throw new Error('Failed to delete party. Please try again.');
    }
  }, [currentUser, parties]);

  // Get parties for a specific group
  const getGroupParties = useCallback(async (groupId) => {
    if (!currentUser || !groupId) return [];

    try {
      const groupPartiesQuery = query(
        collection(db, 'parties'),
        where('groupId', '==', groupId),
        orderBy('date', 'asc')
      );
      
      const querySnapshot = await getDocs(groupPartiesQuery);
      const groupParties = [];
      
      querySnapshot.forEach((doc) => {
        groupParties.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return groupParties;
    } catch (error) {
      console.error('PartyContext: Error fetching group parties:', error);
      return [];
    }
  }, [currentUser]);

  // Check for date conflicts with group members
  const checkDateConflicts = useCallback(async (groupId, partyDate) => {
    if (!currentUser || !groupId || !partyDate) return [];

    try {
      console.log('PartyContext: Checking date conflicts for party date:', partyDate);
      
      // First get the group to find all members
      const groupDoc = await getDoc(doc(db, 'groups', groupId));
      if (!groupDoc.exists()) {
        console.log('PartyContext: Group not found:', groupId);
        return [];
      }
      
      const groupData = groupDoc.data();
      const memberIds = groupData.members || [];
      console.log('PartyContext: Group members:', memberIds);
      
      if (memberIds.length === 0) {
        return [];
      }
      
      // Get unavailable dates for all group members
      const unavailableDatesQuery = query(
        collection(db, 'unavailableDates'),
        where('userId', 'in', memberIds)
      );
      
      const datesSnapshot = await getDocs(unavailableDatesQuery);
      const memberDates = [];
      
      datesSnapshot.forEach((doc) => {
        const data = doc.data();
        memberDates.push({
          id: doc.id,
          ...data
        });
      });
      
      // Get user profiles for member names
      const userProfilesQuery = query(
        collection(db, 'users'),
        where('uid', 'in', memberIds)
      );
      
      const profilesSnapshot = await getDocs(userProfilesQuery);
      const memberProfiles = {};
      
      profilesSnapshot.forEach((doc) => {
        const profile = doc.data();
        memberProfiles[profile.uid] = profile;
      });
      
      // Find conflicts for the specific party date
      const partyDateString = partyDate.toDateString();
      const conflicts = memberDates
        .filter(dateData => {
          if (!dateData.date) return false;
          
          // Handle Firestore Timestamp
          let dateFromData;
          if (dateData.date?.toDate && typeof dateData.date.toDate === 'function') {
            dateFromData = dateData.date.toDate();
          } else if (dateData.date instanceof Date) {
            dateFromData = dateData.date;
          } else if (typeof dateData.date === 'string') {
            dateFromData = new Date(dateData.date);
          } else if (typeof dateData.date === 'number') {
            dateFromData = new Date(dateData.date);
          } else {
            return false;
          }
          
          if (isNaN(dateFromData.getTime())) return false;
          
          // Compare dates by converting to date strings
          const dateDataString = dateFromData.toDateString();
          return dateDataString === partyDateString;
        })
        .map(dateData => ({
          userId: dateData.userId,
          userName: memberProfiles[dateData.userId]?.displayName || 'Unknown User',
          reason: dateData.reason || ''
        }));
      
      console.log('PartyContext: Found conflicts for date:', partyDateString, conflicts);
      return conflicts;
      
    } catch (error) {
      console.error('PartyContext: Error checking date conflicts:', error);
      return [];
    }
  }, [currentUser]);

  // Refresh parties data
  const refreshParties = useCallback(() => {
    fetchParties();
  }, [fetchParties]);

  useEffect(() => {
    fetchParties();
  }, [fetchParties]);

  const value = {
    parties,
    loading,
    error,
    createParty,
    updateParty,
    deleteParty,
    getGroupParties,
    checkDateConflicts,
    refreshParties
  };

  return (
    <PartyContext.Provider value={value}>
      {children}
    </PartyContext.Provider>
  );
}
